import { Draggable } from "@hello-pangea/dnd";
import type { CSSProperties } from "react";
import { useRedirect, RecordContextProvider } from "ra-core";
import { ReferenceField } from "@/components/admin/reference-field";
import { Card, CardContent } from "@/components/ui/card";

import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Deal } from "../types";
import { formatCompactCurrency } from "../misc/formatCurrency";
import { useReadOnly } from "../misc/fromBitrix";

export const DealCard = ({ deal, index }: { deal: Deal; index: number }) => {
  // a Bitrix24 deal in Bitrix24 mode moves between stages in Bitrix24
  const readOnly = useReadOnly(deal);
  if (!deal) return null;

  return (
    <Draggable
      draggableId={String(deal.id)}
      index={index}
      isDragDisabled={readOnly}
    >
      {(provided, snapshot) => (
        <DealCardContent
          provided={provided}
          snapshot={snapshot}
          deal={deal}
          index={index}
        />
      )}
    </Draggable>
  );
};

// Name, company and amount: what a manager scans the board for. The card has
// to fit a ~140 px column, hence the compact amount and clamped lines.
export const DealCardContent = ({
  provided,
  snapshot,
  deal,
  index = 0,
}: {
  provided?: any;
  snapshot?: any;
  deal: Deal;
  /** position in the column, for the cascade on first render */
  index?: number;
}) => {
  const { currency } = useConfigurationContext();
  const redirect = useRedirect();
  const handleClick = () => {
    redirect(`/deals/${deal.id}/show`, undefined, undefined, undefined, {
      _scrollToTop: false,
    });
  };

  return (
    <div
      className="cursor-pointer"
      {...provided?.draggableProps}
      {...provided?.dragHandleProps}
      ref={provided?.innerRef}
      onClick={handleClick}
    >
      <RecordContextProvider value={deal}>
        <Card
          className={`py-3 gap-0 vv-rise ${
            snapshot?.isDragging
              ? "opacity-90 rotate-1 shadow-lg border-primary/60"
              : "shadow-none vv-card-hover"
          }`}
          style={{ "--vv-i": index } as CSSProperties}
        >
          <CardContent className="px-3 flex flex-col gap-1 min-w-0">
            <p className="text-sm font-medium leading-snug line-clamp-2 break-words">
              {deal.name}
            </p>
            {deal.company_id != null ? (
              <p className="text-xs text-muted-foreground truncate">
                <ReferenceField
                  source="company_id"
                  reference="companies"
                  link={false}
                />
              </p>
            ) : null}
            <p className="text-xs font-semibold tabular-nums text-primary-ink">
              {formatCompactCurrency(deal.amount ?? 0, currency)}
            </p>
          </CardContent>
        </Card>
      </RecordContextProvider>
    </div>
  );
};
